import { useCallback, useEffect, useRef, useState } from "react";
import useLocalAudioToggle from "hooks/livekit/useLocalAudioToggle/useLocalAudioToggle";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { useDataTrack } from "hooks/livekit/useDataTrack/useDataTrack";
import { useSessionContext } from "hooks/useSession";
import { logSession } from "utils/sentryLogger";

const SOLO_EVENT_STATES = new Set(["challenge", "gather"]);

export const useAutoMuteLocalMic = () => {
  const { isMuted: isAudioMuted, toggleAudioEnabled, setUpstreamPaused } = useLocalAudioToggle();
  const { localTracks } = useVideoContext();
  const { currentEventState, isShowTransitionView } = useSessionContext();
  const [isOkobotSpeaking, setIsOkobotSpeaking] = useState(false);

  // Whether the student muted themselves. Auto-mute releases must not undo a
  // deliberate mute (e.g. muted during a round, okobot speaks, speech ends —
  // the student expects to still be muted). Cleared on every event-state
  // change: a new round starts everyone unmuted.
  const userMutedRef = useRef(false);
  const prevEventStateRef = useRef(currentEventState);

  const hasAudioTrack = localTracks.some((track) => track.kind === "audio");

  // iPads have been losing the first seconds of OKO speech. Snapshot the
  // client state at speech_start (was the page just resumed from the lock
  // screen? what is the mic doing?) so field reports carry the answer.
  const lastResumeRef = useRef(null);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") lastResumeRef.current = Date.now();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useDataTrack((e) => {
    if (e?.event_type === "speech_start") {
      setIsOkobotSpeaking(true);
      const mic = localTracks.find((track) => track.kind === "audio");
      const raw = mic?.mediaStreamTrack;
      logSession("Okobot speech_start client state", {
        event: "okobot_speech_start_state",
        visibility: document.visibilityState,
        secondsSinceResume: lastResumeRef.current
          ? Math.round((Date.now() - lastResumeRef.current) / 1000)
          : null,
        micReadyState: raw?.readyState,
        micEnabled: raw?.enabled,
        micHardwareMuted: raw?.muted,
        micTrackMuted: mic?.isMuted,
        micUpstreamPaused: mic?.isUpstreamPaused,
      });
    }
    if (e?.event_type === "speech_end") setIsOkobotSpeaking(false);
  });

  const isSoloTime = SOLO_EVENT_STATES.has(currentEventState);
  const isAutoMuted = isSoloTime || isShowTransitionView || isOkobotSpeaking;

  // The mic button routes through this so a manual toggle is recorded as user
  // intent, which plain toggleAudioEnabled calls (our own auto-mutes) are not.
  const toggleMic = useCallback(() => {
    if (isAutoMuted) return;
    userMutedRef.current = !isAudioMuted;
    toggleAudioEnabled();
  }, [isAutoMuted, isAudioMuted, toggleAudioEnabled]);

  useEffect(() => {
    if (!hasAudioTrack || !currentEventState) return;

    if (prevEventStateRef.current !== currentEventState) {
      prevEventStateRef.current = currentEventState;
      userMutedRef.current = false;
    }

    // Auto-mute pauses the upstream rather than muting the track: a real mute
    // disables capture, which on iOS flips a Bluetooth headset's profile and
    // swallows the first seconds of the OKO phrase that triggered it. The
    // student's own mute button still does a real mute.
    setUpstreamPaused(isAutoMuted);
    if (!isAutoMuted && isAudioMuted && !userMutedRef.current) {
      toggleAudioEnabled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEventState, isShowTransitionView, isOkobotSpeaking, hasAudioTrack]);

  return { isAutoMuted, isSoloTime, toggleMic, isAudioMuted, isMuted: isAudioMuted };
};
